package us.laantech.common.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import us.laantech.common.dto.PosButtonAction.*
import us.laantech.common.dto.company.Department

@Serializable
sealed interface PosButtonLayout

@Serializable
enum class SectionType {
    PROGRAMMABLE, // Can be modified by customer
    FIXED // Can only be modified by Admin
}

@Serializable
data class ButtonSection(
    val sectionType: SectionType,
    val buttonGrid: List<List<ButtonConfig>>
)


@Serializable
@SerialName("CashierButtonLayout")
data class CashierButtonLayout(
    val sections: List<ButtonSection>? = null,
): PosButtonLayout {

    fun getProgrammableButtons() = sections?.firstOrNull { it.sectionType == SectionType.PROGRAMMABLE }?.buttonGrid ?: programmableDefaults
    fun getFixedButtons() = sections?.firstOrNull { it.sectionType == SectionType.FIXED }?.buttonGrid ?: fixedDefaults

    val programmableDefaults = listOf(
        // Row 1
        listOf(
            ButtonConfig("Notifications", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("Add by UPC Test", AddByUPCs(listOf("040000000006"))),
            ButtonConfig("Add by SKU Test", AddByUuid(listOf("01a07704-da4e-7637-87e5-7b2fed770a62"))),
            ButtonConfig("Product Variant Test", AddByUPCs(listOf("611269003642"))),
            ButtonConfig("Payout Product Test", AddByUPCs(listOf("030100001331"))),
            ButtonConfig("EBT Item Test", AddByUPCs(listOf("012044038918"))),
        ),
        // Row 2
        listOf(
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("Age Limit Item Test", AddByUPCs(listOf("012005404653"))),
            ButtonConfig("Item Group Test", AddByUuid(listOf("01a07704-da4e-7637-87e5-7b2fed770a62", "01a07704-d8a6-7479-857d-f3e58552df36"))),
            ButtonConfig("Department Test", AddByDepartment(Department("019fca3c-5994-764e-8517-badcc5020b95","Dept Name"))),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
        ),
        // Row 3
        listOf(
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("Tax", AddAmountToCart("Taxable Amount", null, true)),
        ),
        // Row 4
        listOf(
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("Non-Tax", AddAmountToCart("Non-Taxable Amount", null, false)),
        ),
    )

    val fixedDefaults: List<List<ButtonConfig>> = listOf(
        // Row 1
        listOf(
            // Columns
            ButtonConfig("Round Up", RoundUp),
            ButtonConfig("Cupon", Cupon),
            ButtonConfig("EBT", CheckoutWithCard(CardPaymentType.EBT)),
            ButtonConfig("Debit", CheckoutWithCard(CardPaymentType.DEBIT)),
            ButtonConfig("Credit", CheckoutWithCard(CardPaymentType.CREDIT)),
            ButtonConfig("$5", CheckoutWithCash(500)),
            ButtonConfig("$10", CheckoutWithCash(1000)),
            ButtonConfig("$20", CheckoutWithCash(2000)),
            ButtonConfig("$50", CheckoutWithCash(5000)),
            ButtonConfig("Cash", CheckoutWithCash(null)),
        ),
        // Row 2
        listOf(
            // Columns
            ButtonConfig("Discount", DiscountCart),
            ButtonConfig("Suspend", SuspendCart),
            ButtonConfig("Recall", RecallCart),
            ButtonConfig("Refund", Refund),
            ButtonConfig("Vendor Payout", VendorPayout),
            ButtonConfig("Void", VoidCart),
            ButtonConfig("Rewards", Rewards),
            ButtonConfig("No Sale", NoSale),
            ButtonConfig("Safe Drop", SafeDrop),
            ButtonConfig("Next Dollar", CheckoutWithCash(null, true)),
        ),
        // Row 3
        listOf(
            // Columns
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("", NoOp),
            ButtonConfig("Cash Check", CashCheck),
            ButtonConfig("Cash Back", CashBack),
        ),
    )

}

@Serializable
@SerialName("KioskButtonLayout")
data class KioskButtonLayout(
    val todo: List<List<ButtonConfig>>,
): PosButtonLayout

@Serializable
@SerialName("RestaurantButtonLayout")
data class RestaurantButtonLayout(
    val todo: List<List<ButtonConfig>>,
): PosButtonLayout

@Serializable
sealed interface PosButtonAction {
    @Serializable @SerialName("NoOp") data object NoOp: PosButtonAction

    @Serializable @SerialName("RoundUp") data object RoundUp: PosButtonAction
    @Serializable @SerialName("Cupon") data object Cupon: PosButtonAction
    @Serializable @SerialName("DiscountCart") data object DiscountCart: PosButtonAction
    @Serializable @SerialName("SuspendCart") data object SuspendCart: PosButtonAction
    @Serializable @SerialName("RecallCart") data object RecallCart: PosButtonAction
    @Serializable @SerialName("Refund") data object Refund: PosButtonAction
    @Serializable @SerialName("VendorPayout") data object VendorPayout: PosButtonAction
    @Serializable @SerialName("VoidCart") data object VoidCart: PosButtonAction
    @Serializable @SerialName("Rewards") data object Rewards: PosButtonAction
    @Serializable @SerialName("NoSale") data object NoSale: PosButtonAction
    @Serializable @SerialName("SafeDrop") data object SafeDrop: PosButtonAction
    @Serializable @SerialName("CashBack") data object CashBack: PosButtonAction
    @Serializable @SerialName("CashCheck") data object CashCheck: PosButtonAction

    @Serializable @SerialName("AddByUPCs") data class AddByUPCs(val upcs: List<String>): PosButtonAction
    @Serializable @SerialName("AddByUuid") data class AddByUuid(val uuids: List<String>): PosButtonAction

//    @Serializable data class AddToCartBy(val upcs: List<String>?, val uuids: List<String>?): PosButtonAction

    @Serializable @SerialName("CheckoutWithCard") data class CheckoutWithCard(val paymentType: CardPaymentType): PosButtonAction
    @Serializable @SerialName("CheckoutWithCash") data class CheckoutWithCash(val amountCents: Long?, val nextDollar: Boolean = false): PosButtonAction

    /**
     * Action that adds a configurable monetary amount directly to the cart.
     *
     * This action creates a cart item with a fixed amount rather than being tied to a specific retail product.
     * The amount is specified in cents and can optionally be marked as taxable. This is useful for
     * custom charges, fees, or other monetary items that don't correspond to inventory products.
     *
     * @property label The display name or description for this amount item in the cart
     * @property amountCents The monetary value in cents to add to the cart. A null value may indicate
     *                       that the amount should be entered at the time of use
     * @property taxable Whether this amount should be subject to tax calculations. Defaults to true
     */
    @Serializable @SerialName("AddAmountToCart") data class AddAmountToCart(val label: String, val amountCents: Long?, val taxable: Boolean = true): PosButtonAction

    @Serializable @SerialName("AddByDepartment") data class AddByDepartment(val department: Department?): PosButtonAction

}

@Serializable
data class ButtonConfig(
    val label: String?,
    val action: PosButtonAction,
    val enabled: Boolean = true,
    val icon: String? = null,
    val color: String? = null,
){
    val labelAlt: String? = when(action) {
        is PosButtonAction.AddAmountToCart -> action.label
        is PosButtonAction.AddByDepartment -> action.department?.departmentName
        else -> "Not Set"
    }
}